import { useState, useRef, useCallback } from 'react';
import { ArrowUp, Paperclip, Briefcase, Receipt, CalendarCheck, FolderKanban } from 'lucide-react';
import { palette, admin, transition, fontFamily } from '../../../styles/tokens';
import { DropdownMenu } from '../../../components/common/DropdownMenu';

interface Props {
  onSend: (content: string) => void;
  sending: boolean;
  disabled?: boolean;
}

export function Composer({ onSend, sending, disabled }: Props) {
  const [text, setText] = useState('');
  const [showAttach, setShowAttach] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    if (!text.trim() || sending || disabled) return;
    onSend(text.trim());
    setText('');
    inputRef.current?.focus();
  }, [text, sending, disabled, onSend]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const hasText = text.trim().length > 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: 8,
      padding: '10px 16px', borderTop: `1px solid ${admin.border}`,
      background: palette.white, position: 'relative',
    }}>
      {/* Attachment button */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowAttach(!showAttach)}
          style={{
            width: 36, height: 36, borderRadius: 18, border: 'none',
            background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Paperclip size={18} color={palette.g400} strokeWidth={1.8} />
        </button>
        <DropdownMenu
          isOpen={showAttach}
          onClose={() => setShowAttach(false)}
          position="top-left"
          width={200}
          sections={[{ items: [
            { icon: <Briefcase size={15} color={palette.green} />, label: 'Job', subtitle: 'Attach or create', onClick: () => {} },
            { icon: <Receipt size={15} color={palette.accent} />, label: 'Invoice', subtitle: 'Attach or create', onClick: () => {} },
            { icon: <CalendarCheck size={15} color={palette.amber} />, label: 'Booking', subtitle: 'Attach or create', onClick: () => {} },
            { icon: <FolderKanban size={15} color="#8B5CF6" />, label: 'Project', subtitle: 'Attach or create', onClick: () => {} },
          ] }]}
        />
      </div>

      {/* Input */}
      <textarea
        ref={inputRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        rows={1}
        disabled={disabled}
        style={{
          flex: 1, resize: 'none',
          border: '1px solid #E5E5EA', borderRadius: 24,
          padding: '10px 16px', fontSize: 14,
          fontFamily, outline: 'none', color: palette.g800,
          lineHeight: 1.4, maxHeight: 120, minHeight: 40,
          overflowY: 'auto',
          background: disabled ? palette.g100 : '#F7F8FA',
          transition: `border-color ${transition.fast}`,
        }}
        onFocus={e => (e.currentTarget.style.borderColor = palette.accent)}
        onBlur={e => (e.currentTarget.style.borderColor = '#E5E5EA')}
      />

      {/* Send button — only mounted with text (display:none keeps it out of focus order) */}
      {hasText && (
        <button
          onClick={handleSend}
          disabled={sending || disabled}
          style={{
            width: 36, height: 36, borderRadius: 18, border: 'none',
            background: !disabled ? '#0F6CBD' : palette.g200,
            cursor: !disabled ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: `background ${transition.fast}`,
            flexShrink: 0,
          }}
        >
          <ArrowUp size={18} color={palette.white} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
